import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TranslationProvider } from "./contexts/TranslationContext";
import Login from "./components/auth/Login";
import Dashboard from "./components/dashboard/Dashboard";
import FormBuilder from "./components/forms/FormBuilder";
import FormList from "./components/forms/FormList";
import FormDetail from "./components/forms/FormDetail";
import UserProfile from "./components/users/UserProfile";
import UserManagement from "./components/users/UserManagement";
import WooCommerceOrders from "./components/woocommerce/WooCommerceOrders";
import Layout from "./components/layout/Layout";

const queryClient = new QueryClient();

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return user ? <Navigate to="/forms" /> : <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== "admin") {
    return <Navigate to="/forms" />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TranslationProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <Routes>
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                {/* Registration disabled */}
                <Route path="/register" element={<Navigate to="/login" />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/forms"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FormList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/forms/new"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FormBuilder />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/forms/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FormDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/forms/:id/edit"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FormBuilder />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserProfile />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <AdminRoute>
                      <Layout>
                        <UserManagement />
                      </Layout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/woocommerce/orders"
                  element={
                    <AdminRoute>
                      <Layout>
                        <WooCommerceOrders />
                      </Layout>
                    </AdminRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/forms" />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </TranslationProvider>
    </QueryClientProvider>
  );
}

export default App;
